import { Show, useContext } from 'solid-js';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { StructuredData, buildFullUrl, getImageUrl } from './structured-data';
import { Seo } from '../seo-settings/seo-settings-types';

type WebPageSchemaProps = {
    title: string;
    description?: string;
    seo?: Seo;
    pathname: string;
    isMedicalPage?: boolean;
};

/**
 * WebPage/MedicalWebPage schema for pages.
 * Use isMedicalPage=true for healthcare-related content pages.
 */
export const WebPageSchema = (props: WebPageSchemaProps) => {
    const { siteInfo } = useContext(AppContext);

    const isAtosCare = siteInfo.siteType === 'atos-care';
    const orgName = isAtosCare ? 'Atos Care' : 'Atos Medical';
    const baseUrl = `https://${siteInfo.currentDomain}`;
    const pageUrl = buildFullUrl('https', siteInfo.currentDomain, props.pathname);

    const description = () => {
        if (props.seo?.metaDescription) return props.seo.metaDescription;
        if (props.description) return props.description;
        return undefined;
    };

    const imageUrl = () => {
        if (props.seo?.og_image) {
            return getImageUrl(props.seo.og_image);
        }
        return undefined;
    };

    const schema = () => ({
        '@context': 'https://schema.org',
        '@type': props.isMedicalPage ? 'MedicalWebPage' : 'WebPage',
        name: props.title,
        url: pageUrl,
        ...(description() && { description: description() }),
        ...(imageUrl() && {
            primaryImageOfPage: {
                '@type': 'ImageObject',
                url: imageUrl(),
            },
        }),
        isPartOf: {
            '@type': 'WebSite',
            name: orgName,
            url: baseUrl,
        },
        publisher: {
            '@type': isAtosCare ? 'Organization' : 'MedicalOrganization',
            name: orgName,
            url: baseUrl,
            logo: {
                '@type': 'ImageObject',
                url: `${baseUrl}/assets/atos-logo.svg`,
            },
        },
    });

    return (
        <Show when={props.title}>
            <StructuredData data={schema()} />
        </Show>
    );
};
